import { z } from 'zod';

// ============================================================================
// PROFILE SCHEMAS
// ============================================================================

export const profileUpdateSchema = z.object({
  username: z.string()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be at most 30 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .optional(),
  
  display_name: z.string()
    .min(1, 'Display name cannot be empty')
    .max(50, 'Display name must be at most 50 characters')
    .optional(),
  
  bio: z.string()
    .max(500, 'Bio must be at most 500 characters')
    .optional(),
  
  favorite_quote: z.string()
    .max(280, 'Favorite quote must be at most 280 characters')
    .optional(),
  
  profile_image_url: z.string()
    .url('Must be a valid URL')
    .max(500, 'URL too long')
    .optional(),
  
  banner_image_url: z.string()
    .url('Must be a valid URL')
    .max(500, 'URL too long')
    .optional(),
  
  custom_links: z.array(z.object({
    label: z.string().max(50, 'Label too long'),
    url: z.string().url('Must be a valid URL').max(500, 'URL too long')
  }))
    .max(5, 'Maximum 5 custom links allowed')
    .optional(),
  
  mini_profile_theme: z.enum(['default', 'dark', 'colorful', 'minimal'])
    .optional(),
  
  mini_profile_enabled: z.boolean().optional(),
  profile_discoverable: z.boolean().optional(),
  mature_content_enabled: z.boolean().optional(),
}).strict(); // Reject unknown fields to prevent injection

export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;

// ============================================================================
// BULLETIN SCHEMAS
// ============================================================================

export const bulletinCreateSchema = z.object({
  content: z.string()
    .min(1, 'Content cannot be empty')
    .max(10000, 'Content must be at most 10,000 characters')
    .trim(),
  
  media_url: z.string()
    .url('Must be a valid URL')
    .max(1000, 'URL too long')
    .optional(),
  
  media_type: z.enum(['image', 'video', 'gif'])
    .optional(),
  
  expires_at: z.string()
    .datetime('Must be a valid ISO 8601 datetime')
    .optional(),
  
  is_nsfw: z.boolean()
    .default(false),
  
  community_tags: z.array(z.string().uuid('Invalid tag ID'))
    .max(5, 'Maximum 5 tags allowed')
    .optional(),
}).strict();

export type BulletinCreateInput = z.infer<typeof bulletinCreateSchema>;

export const bulletinUpdateSchema = bulletinCreateSchema.partial();

// ============================================================================
// ANNOTATION SCHEMAS
// ============================================================================

export const postAnnotationCreateSchema = z.object({
  postId: z.string().uuid('Invalid post ID'),
  postType: z.enum(['bulletin', 'loop']),
  content: z.string()
    .min(1, 'Annotation cannot be empty')
    .max(1000, 'Annotation must be at most 1,000 characters')
    .trim(),
  metadata: z.record(z.any()).optional(),
}).strict();

export const postAnnotationUpdateSchema = z.object({
  content: z.string()
    .min(1, 'Annotation cannot be empty')
    .max(1000, 'Annotation must be at most 1,000 characters')
    .trim()
    .optional(),
  metadata: z.record(z.any()).optional(),
  isVisible: z.boolean().optional(),
}).strict();

export type PostAnnotationCreateInput = z.infer<typeof postAnnotationCreateSchema>;
export type PostAnnotationUpdateInput = z.infer<typeof postAnnotationUpdateSchema>;

// ============================================================================
// COMMENT SCHEMAS
// ============================================================================

export const commentCreateSchema = z.object({
  content: z.string()
    .min(1, 'Comment cannot be empty')
    .max(5000, 'Comment must be at most 5,000 characters')
    .trim(),
  
  bulletin_id: z.string()
    .uuid('Invalid bulletin ID'),
  
  parent_id: z.string()
    .uuid('Invalid parent comment ID')
    .optional(),
}).strict();

export type CommentCreateInput = z.infer<typeof commentCreateSchema>;

// ============================================================================
// DROP SCHEMAS
// ============================================================================

export const dropRaritySchema = z.object({
  rarity: z.enum(['common', 'uncommon', 'rare', 'legendary']),
  count: z.number()
    .int('Count must be an integer')
    .min(1, 'Count must be at least 1')
    .max(10000, 'Count must be at most 10,000'),
  
  unit_value_cents: z.number()
    .int('Value must be an integer')
    .min(1, 'Value must be at least $0.01')
    .max(100000, 'Value must be at most $1,000'),
  
  media_url: z.string()
    .url('Must be a valid URL')
    .max(500, 'URL too long'),
  
  thumbnail_url: z.string()
    .url('Must be a valid URL')
    .max(500, 'URL too long')
    .optional(),
});

export const dropCollectionCreateSchema = z.object({
  name: z.string()
    .min(1, 'Name cannot be empty')
    .max(100, 'Name must be at most 100 characters')
    .trim(),
  
  description: z.string()
    .max(1000, 'Description must be at most 1,000 characters')
    .optional(),
  
  icon_url: z.string()
    .url('Must be a valid URL')
    .max(500, 'URL too long'),
  
  rarities: z.array(dropRaritySchema)
    .min(1, 'At least one rarity tier required')
    .max(10, 'Maximum 10 rarity tiers allowed'),
  
  is_test: z.boolean()
    .default(false),
}).strict();

export type DropCollectionCreateInput = z.infer<typeof dropCollectionCreateSchema>;

// ============================================================================
// CANVAS SCHEMAS
// ============================================================================

export const canvasItemSchema = z.object({
  type: z.enum(['image', 'text', 'sticker', 'shape']),
  
  content: z.string()
    .max(10000, 'Content too large'),
  
  position: z.object({
    x: z.number().finite(),
    y: z.number().finite(),
  }),
  
  size: z.object({
    width: z.number().positive().max(10000),
    height: z.number().positive().max(10000),
  }),
  
  rotation: z.number()
    .min(-360)
    .max(360)
    .optional(),
  
  z_index: z.number()
    .int()
    .min(0)
    .max(1000)
    .optional(),
}).strict();

// ============================================================================
// UTILITY SCHEMAS
// ============================================================================

export const uuidSchema = z.string().uuid('Invalid UUID');

export const emailSchema = z.string()
  .email('Invalid email address')
  .max(255, 'Email too long');

export const urlSchema = z.string()
  .url('Invalid URL')
  .max(2000, 'URL too long');

export const positiveIntSchema = z.number()
  .int('Must be an integer')
  .positive('Must be positive');

export const paginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

export const searchQuerySchema = z.string()
  .min(1, 'Search query cannot be empty')
  .max(200, 'Search query too long')
  .trim();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get user-friendly error message from Zod error
 */
export function getValidationErrorMessage(error: z.ZodError): string {
  const firstError = error.errors[0];
  
  if (firstError.message) {
    return firstError.message;
  }
  
  const field = firstError.path.join('.');
  
  switch (firstError.code) {
    case 'too_small':
      return `${field} must be at least ${firstError.minimum}`;
    case 'too_big':
      return `${field} must be at most ${firstError.maximum}`;
    case 'invalid_string':
      if (firstError.validation === 'url') {
        return `${field} must be a valid URL`;
      }
      if (firstError.validation === 'email') {
        return `${field} must be a valid email`;
      }
      if (firstError.validation === 'uuid') {
        return `${field} must be a valid UUID`;
      }
      break;
    case 'invalid_type':
      return `${field} must be a ${firstError.expected}`;
    case 'invalid_enum_value':
      return `${field} must be one of: ${firstError.options.join(', ')}`;
    default:
      return `Invalid ${field}`;
  }
  
  return 'Invalid input';
}

/**
 * Safe parse with user-friendly error
 */
export function safeParse<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  return {
    success: false,
    error: getValidationErrorMessage(result.error),
  };
}
